import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import FaqBusinessHoursSection from "./FaqBusinessHoursSection";
import SkillLifecycleTransferField from "../SkillLifecycleTransferField";
import { useAgents } from "../../hooks/useAgents";
import { useUsers } from "../../hooks/useUsers";
import { translateSkillForm } from "../../utils/skillLifecycle";
import {
  DEFAULT_SOCIAL_MEDIA_HANDOFF_RULE,
  SOCIAL_MEDIA_TONE_OPTIONS,
  handoffRuleToTransfer,
  normalizeHandoffRules,
  transferToHandoffRule,
} from "../../utils/socialMediaSkillConfig";

function ConfigAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function SocialMediaSkillConfigSection({
  skillConfig = {},
  mergeSkillConfig,
  readOnly = false,
  currentAgentId = null,
}) {
  const { t } = useTranslation("settings");
  const formCopy = (suffix, fallbackKey) => translateSkillForm(t, "social_media", suffix, fallbackKey);

  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 200, "", "todos");
  const { data: agentsData, isLoading: loadingAgents } = useAgents(1, 100, "");

  const users = usersData?.users ?? [];
  const aiAgents = useMemo(() => {
    const rows = agentsData?.agents ?? [];
    return rows.filter(
      (a) =>
        String(a.id) !== String(currentAgentId || "") &&
        (a.status === "active" || a.status === true)
    );
  }, [agentsData?.agents, currentAgentId]);

  const shortcutLabels = useMemo(
    () => ({
      queue: t("agentsListPage.lifecycleTransferQueue"),
      receptionist: t("agentsListPage.lifecycleTransferReceptionist"),
      shortcutsGroup: t("agentsListPage.lifecycleTransferShortcuts", { defaultValue: "Atalhos" }),
    }),
    [t]
  );

  const handoffRules = useMemo(
    () => normalizeHandoffRules(skillConfig.handoff_rules),
    [skillConfig.handoff_rules]
  );

  const patchRules = (nextRules) => {
    mergeSkillConfig("social_media", {
      handoff_rules: nextRules.map((rule) => ({
        ...rule,
        keywords: String(rule.keywords || "")
          .split(/[,\n;]+/)
          .map((x) => x.trim())
          .filter(Boolean),
      })),
    });
  };

  const updateRule = (index, partial) => {
    const next = handoffRules.map((rule, i) => (i === index ? { ...rule, ...partial } : rule));
    patchRules(next);
  };

  const addRule = () => {
    patchRules([...handoffRules, { ...DEFAULT_SOCIAL_MEDIA_HANDOFF_RULE }]);
  };

  const removeRule = (index) => {
    patchRules(handoffRules.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={1}>
      <ConfigAccordion
        title={formCopy("accordionBrandTitle", "agentsListPage.socialMediaAccordionBrandTitle")}
        subtitle={formCopy("accordionBrandSubtitle", "agentsListPage.socialMediaAccordionBrandSubtitle")}
        defaultExpanded
      >
        <Stack spacing={2}>
          <TextField
            label={t("agentsListPage.socialMediaBrandVoice")}
            helperText={t("agentsListPage.socialMediaBrandVoiceHelper")}
            value={skillConfig.brand_voice || ""}
            onChange={(e) => mergeSkillConfig("social_media", { brand_voice: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" disabled={readOnly}>
            <InputLabel id="social-media-tone-label">{t("agentsListPage.socialMediaTone")}</InputLabel>
            <Select
              labelId="social-media-tone-label"
              label={t("agentsListPage.socialMediaTone")}
              value={skillConfig.tone || "casual"}
              onChange={(e) => mergeSkillConfig("social_media", { tone: e.target.value })}
            >
              {SOCIAL_MEDIA_TONE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={skillConfig.use_emoji !== false}
                onChange={(e) => mergeSkillConfig("social_media", { use_emoji: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={t("agentsListPage.socialMediaUseEmoji")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={skillConfig.story_reply_ack !== false}
                onChange={(e) => mergeSkillConfig("social_media", { story_reply_ack: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={t("agentsListPage.socialMediaStoryReplyAck")}
          />
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={formCopy("accordionHandoffTitle", "agentsListPage.socialMediaAccordionHandoffTitle")}
        subtitle={formCopy("accordionHandoffSubtitle", "agentsListPage.socialMediaAccordionHandoffSubtitle")}
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={skillConfig.handoff_enabled !== false}
                onChange={(e) => mergeSkillConfig("social_media", { handoff_enabled: e.target.checked })}
                disabled={readOnly}
              />
            }
            label={t("agentsListPage.socialMediaHandoffEnabled")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={skillConfig.handoff_use_catalog !== false}
                onChange={(e) => mergeSkillConfig("social_media", { handoff_use_catalog: e.target.checked })}
                disabled={readOnly || skillConfig.handoff_enabled === false}
              />
            }
            label={t("agentsListPage.socialMediaHandoffUseCatalog")}
          />
          <Typography variant="body2" color="text.secondary">
            {t("agentsListPage.socialMediaHandoffRulesHint")}
          </Typography>
          {handoffRules.map((rule, index) => (
            <Box
              key={`handoff-rule-${index}`}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">
                    {t("agentsListPage.socialMediaHandoffRuleLabel", { index: index + 1 })}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => removeRule(index)}
                    disabled={readOnly}
                  >
                    {t("agentsListPage.socialMediaRemoveRule")}
                  </Button>
                </Stack>
                <TextField
                  label={t("agentsListPage.socialMediaRuleIntent")}
                  value={rule.intent || ""}
                  onChange={(e) => updateRule(index, { intent: e.target.value })}
                  size="small"
                  fullWidth
                  disabled={readOnly}
                />
                <TextField
                  label={t("agentsListPage.socialMediaRuleKeywords")}
                  helperText={t("agentsListPage.socialMediaRuleKeywordsHelper")}
                  value={rule.keywords || ""}
                  onChange={(e) => updateRule(index, { keywords: e.target.value })}
                  size="small"
                  fullWidth
                  disabled={readOnly}
                />
                <SkillLifecycleTransferField
                  label={t("agentsListPage.socialMediaRuleDestination")}
                  transfer={handoffRuleToTransfer(rule)}
                  users={users}
                  agents={aiAgents}
                  excludeAgentIds={currentAgentId ? [String(currentAgentId)] : []}
                  shortcutLabels={shortcutLabels}
                  loading={loadingUsers || loadingAgents}
                  disabled={readOnly || skillConfig.handoff_enabled === false}
                  onChange={(transfer) => updateRule(index, transferToHandoffRule(transfer, rule))}
                />
              </Stack>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addRule}
            disabled={readOnly || skillConfig.handoff_enabled === false}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("agentsListPage.socialMediaAddRule")}
          </Button>
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={t("agentsListPage.faqBusinessHoursTitle")}
        subtitle={formCopy("accordionHoursSubtitle", "agentsListPage.socialMediaAccordionHoursSubtitle")}
      >
        <FaqBusinessHoursSection
          skillConfig={skillConfig}
          disabled={readOnly}
          onChange={(partial) => mergeSkillConfig("social_media", partial)}
        />
      </ConfigAccordion>
    </Stack>
  );
}
